/**
 * Config hooks — TanStack Query wrappers for the config admin API.
 *
 * All hooks require super_admin role — enforced at route level in ConfigPage.
 *
 * Hooks:
 * Plans:
 *   usePlans()          — list all plans
 *   useCreatePlan()     — create plan mutation
 *   useUpdatePlan()     — update plan mutation
 *
 * Add-ons:
 *   useAddons()         — list all add-ons
 *   useCreateAddon()    — create add-on mutation
 *   useUpdateAddon()    — update add-on mutation
 *
 * Countries:
 *   useConfigCountries() — list countries (config admin version, separate from territories)
 *   useCreateCountry()  — create country mutation
 *
 * Regions:
 *   useConfigRegions()  — list regions with country name
 *   useCreateRegion()   — create region mutation
 *
 * Webhook Endpoints:
 *   useWebhookEndpoints()      — list all webhook endpoints
 *   useCreateWebhookEndpoint() — create webhook mutation
 *   useDeleteWebhookEndpoint() — deactivate webhook mutation
 *
 * API Clients:
 *   useApiClients()      — list all API clients (no key hash ever visible)
 *   useCreateApiClient() — create mutation, returns { apiClient, rawKey }
 *   useToggleApiClient() — toggle active status mutation
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiPost, apiPut, apiDelete } from '@/lib/api'

// ─── Row types (mirror the API response) ─────────────────────────────────────

export interface PlanRow {
  id: string
  name: string
  slug: string
  price_cents: number
  billing_period: string
  description: string | null
  active: boolean
  sort_order: number
  created_at: string
  updated_at: string
}

export interface AddonRow {
  id: string
  plan_id: string | null
  name: string
  price_cents: number
  description: string | null
  active: boolean
  created_at: string
  updated_at: string
}

export interface CountryRow {
  id: string
  name: string
  code: string
  phone_code: string | null
  active: boolean
  created_at: string
  updated_at: string
}

export interface RegionRow {
  id: string
  name: string
  country_id: string | null
  active: boolean
  created_at: string
  updated_at: string
  country_name: string | null
}

export interface WebhookEndpointRow {
  id: string
  url: string
  secret: string
  description: string | null
  active: boolean
  events: string[]
  created_at: string
  updated_at: string
}

export interface ApiClientRow {
  id: string
  name: string
  description: string | null
  permissions: string[]
  rate_limit: number
  active: boolean
  last_used_at: string | null
  created_at: string
  updated_at: string
}

// ─── Input types ──────────────────────────────────────────────────────────────

export interface CreatePlanInput {
  name: string
  slug: string
  priceCents: number
  billingPeriod?: string
  description?: string | null
  sortOrder?: number
}

export interface UpdatePlanInput extends Partial<CreatePlanInput> {
  active?: boolean
}

export interface CreateAddonInput {
  name: string
  priceCents: number
  description?: string | null
  planId?: string | null
}

export interface UpdateAddonInput extends Partial<CreateAddonInput> {
  active?: boolean
}

export interface CreateCountryInput {
  name: string
  code: string
  phoneCode?: string | null
}

export interface CreateRegionInput {
  name: string
  countryId: string
}

export interface CreateWebhookInput {
  url: string
  secret?: string
  events: string[]
  description?: string | null
}

export interface UpdateWebhookInput extends Partial<CreateWebhookInput> {
  active?: boolean
}

export interface CreateApiClientInput {
  name: string
  description?: string | null
  permissions?: string[]
  rateLimit?: number
}

export interface CreateApiClientResult {
  apiClient: ApiClientRow
  rawKey: string
}

// ─── Response wrapper ─────────────────────────────────────────────────────────

interface DataResponse<T> {
  data: T
}

// ─── Query key factory ────────────────────────────────────────────────────────

export const configKeys = {
  all: ['config'] as const,
  plans: () => [...configKeys.all, 'plans'] as const,
  addons: () => [...configKeys.all, 'addons'] as const,
  countries: () => [...configKeys.all, 'countries'] as const,
  regions: () => [...configKeys.all, 'regions'] as const,
  webhooks: () => [...configKeys.all, 'webhooks'] as const,
  apiClients: () => [...configKeys.all, 'api-clients'] as const,
}

// ─── Plans ────────────────────────────────────────────────────────────────────

export function usePlans() {
  return useQuery<PlanRow[]>({
    queryKey: configKeys.plans(),
    queryFn: async () => {
      const res = await apiGet<DataResponse<PlanRow[]>>('/api/v1/config/plans')
      return res.data
    },
  })
}

export function useCreatePlan() {
  const queryClient = useQueryClient()

  return useMutation<PlanRow, Error, CreatePlanInput>({
    mutationFn: async (data) => {
      const res = await apiPost<DataResponse<PlanRow>>('/api/v1/config/plans', data)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: configKeys.plans() })
    },
  })
}

export function useUpdatePlan() {
  const queryClient = useQueryClient()

  return useMutation<PlanRow, Error, { id: string; data: UpdatePlanInput }>({
    mutationFn: async ({ id, data }) => {
      const res = await apiPut<DataResponse<PlanRow>>(`/api/v1/config/plans/${id}`, data)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: configKeys.plans() })
    },
  })
}

// ─── Add-ons ──────────────────────────────────────────────────────────────────

export function useAddons() {
  return useQuery<AddonRow[]>({
    queryKey: configKeys.addons(),
    queryFn: async () => {
      const res = await apiGet<DataResponse<AddonRow[]>>('/api/v1/config/addons')
      return res.data
    },
  })
}

export function useCreateAddon() {
  const queryClient = useQueryClient()

  return useMutation<AddonRow, Error, CreateAddonInput>({
    mutationFn: async (data) => {
      const res = await apiPost<DataResponse<AddonRow>>('/api/v1/config/addons', data)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: configKeys.addons() })
    },
  })
}

export function useUpdateAddon() {
  const queryClient = useQueryClient()

  return useMutation<AddonRow, Error, { id: string; data: UpdateAddonInput }>({
    mutationFn: async ({ id, data }) => {
      const res = await apiPut<DataResponse<AddonRow>>(`/api/v1/config/addons/${id}`, data)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: configKeys.addons() })
    },
  })
}

// ─── Countries ────────────────────────────────────────────────────────────────

/**
 * Config-admin version of countries list.
 * Uses separate query key from territories hook to avoid cache collision.
 */
export function useConfigCountries() {
  return useQuery<CountryRow[]>({
    queryKey: configKeys.countries(),
    queryFn: async () => {
      const res = await apiGet<DataResponse<CountryRow[]>>('/api/v1/config/countries')
      return res.data
    },
  })
}

export function useCreateCountry() {
  const queryClient = useQueryClient()

  return useMutation<CountryRow, Error, CreateCountryInput>({
    mutationFn: async (data) => {
      const res = await apiPost<DataResponse<CountryRow>>('/api/v1/config/countries', data)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: configKeys.countries() })
    },
  })
}

// ─── Regions ──────────────────────────────────────────────────────────────────

export function useConfigRegions() {
  return useQuery<RegionRow[]>({
    queryKey: configKeys.regions(),
    queryFn: async () => {
      const res = await apiGet<DataResponse<RegionRow[]>>('/api/v1/config/regions')
      return res.data
    },
  })
}

export function useCreateRegion() {
  const queryClient = useQueryClient()

  return useMutation<RegionRow, Error, CreateRegionInput>({
    mutationFn: async (data) => {
      const res = await apiPost<DataResponse<RegionRow>>('/api/v1/config/regions', data)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: configKeys.regions() })
    },
  })
}

// ─── Webhook Endpoints ────────────────────────────────────────────────────────

export function useWebhookEndpoints() {
  return useQuery<WebhookEndpointRow[]>({
    queryKey: configKeys.webhooks(),
    queryFn: async () => {
      const res = await apiGet<DataResponse<WebhookEndpointRow[]>>('/api/v1/config/webhooks')
      return res.data
    },
  })
}

export function useCreateWebhookEndpoint() {
  const queryClient = useQueryClient()

  return useMutation<WebhookEndpointRow, Error, CreateWebhookInput>({
    mutationFn: async (data) => {
      const res = await apiPost<DataResponse<WebhookEndpointRow>>('/api/v1/config/webhooks', data)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: configKeys.webhooks() })
    },
  })
}

export function useUpdateWebhookEndpoint() {
  const queryClient = useQueryClient()

  return useMutation<WebhookEndpointRow, Error, { id: string; data: UpdateWebhookInput }>({
    mutationFn: async ({ id, data }) => {
      const res = await apiPut<DataResponse<WebhookEndpointRow>>(`/api/v1/config/webhooks/${id}`, data)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: configKeys.webhooks() })
    },
  })
}

export function useDeleteWebhookEndpoint() {
  const queryClient = useQueryClient()

  return useMutation<WebhookEndpointRow, Error, string>({
    mutationFn: async (id) => {
      const res = await apiDelete<DataResponse<WebhookEndpointRow>>(`/api/v1/config/webhooks/${id}`)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: configKeys.webhooks() })
    },
  })
}

// ─── API Clients ──────────────────────────────────────────────────────────────

export function useApiClients() {
  return useQuery<ApiClientRow[]>({
    queryKey: configKeys.apiClients(),
    queryFn: async () => {
      const res = await apiGet<DataResponse<ApiClientRow[]>>('/api/v1/config/api-clients')
      return res.data
    },
  })
}

export function useCreateApiClient() {
  const queryClient = useQueryClient()

  return useMutation<CreateApiClientResult, Error, CreateApiClientInput>({
    mutationFn: async (data) => {
      const res = await apiPost<DataResponse<CreateApiClientResult>>('/api/v1/config/api-clients', data)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: configKeys.apiClients() })
    },
  })
}

export function useToggleApiClient() {
  const queryClient = useQueryClient()

  return useMutation<ApiClientRow, Error, { id: string; active: boolean }>({
    mutationFn: async ({ id, active }) => {
      const res = await apiPut<DataResponse<ApiClientRow>>(`/api/v1/config/api-clients/${id}/toggle`, { active })
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: configKeys.apiClients() })
    },
  })
}
